import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  PlayIcon,
  RocketLaunchIcon,
  FilmIcon,
  FaceSmileIcon,
  SpeakerWaveIcon,
  CheckCircleIcon,
  BookmarkIcon,
  ArrowTopRightOnSquareIcon,
  UserCircleIcon,
  VideoCameraIcon,
  XMarkIcon,
  CheckIcon,
  ArrowPathIcon,
  LinkIcon,
  CloudArrowUpIcon,
} from '@heroicons/react/24/outline';

import { cn } from '@/utils/cn';
import { videoApi } from '@/services/videoApi';
import { avatarApi } from '@/services/avatarApi';
import type { Voice } from '@/services/audioApi';
import type { Avatar } from '@/services/avatarApi';
import VideoUploadZone from '@/components/Video/VideoUploadZone';
import { WizardStepper } from '@/components/Video/WizardStepper';
import { EnginePicker } from '@/components/Video/EnginePicker';
import { HelpNote } from '@/components/Video/HelpNote';
import { TemplateGate } from '@/components/Video/TemplateGate';
import LocalizedTextArea from '@/components/Common/LocalizedTextArea';
import LocalizedTextInput from '@/components/Common/LocalizedTextInput';
import {
  sttEngines,
  mtEngines,
  ttsEngines,
  targetLanguages,
  builtInTemplates,
  loadSavedTemplates,
  persistSavedTemplates,
  diffConfig,
  getLanguageName,
  engineName,
  type TranslationConfig,
  type TranslationTemplate,
  type SttProvider,
  type MtProvider,
  type TtsProvider,
} from '@/utils/videoTranslationConfig';

type VideoMode = 'dubbed' | 'avatar' | 'lipsynced';
type NarrationMode = 'formal' | 'modern-colloquial' | 'classic-colloquial' | 'code-mixed';

export interface PreselectedUpload {
  fileId: string;
  name: string;
  size?: number;
}

const VIDEO_MODES: { mode: VideoMode; label: string; description: string; icon: React.ReactNode }[] = [
  {
    mode: 'dubbed',
    label: 'Dubbed',
    description: 'Original video + translated audio. Fast, no AI video processing.',
    icon: <SpeakerWaveIcon className="h-6 w-6" />,
  },
  {
    mode: 'avatar',
    label: 'Avatar',
    description: 'Select an avatar — AI generates a lip-synced video of that person speaking.',
    icon: <FaceSmileIcon className="h-6 w-6" />,
  },
  {
    mode: 'lipsynced',
    label: 'LipSynced',
    description: 'AI lip-syncs the original video face to match the translated audio.',
    icon: <FilmIcon className="h-6 w-6" />,
  },
];

const NARRATION_MODES: { value: NarrationMode; label: string; description: string }[] = [
  { value: 'formal', label: 'Formal', description: 'Neutral, professional tone — widest language support' },
  { value: 'modern-colloquial', label: 'Modern Colloquial', description: 'Casual, everyday spoken style' },
  { value: 'classic-colloquial', label: 'Classic Colloquial', description: 'Traditional conversational style' },
  { value: 'code-mixed', label: 'Code-Mixed', description: 'Natural mixing with English, e.g. Hinglish' },
];

const STEP_LABELS = [
  { label: 'Source video', hint: 'Upload & languages' },
  { label: 'Speech to text', hint: 'Transcription engine' },
  { label: 'Translation', hint: 'Machine translation' },
  { label: 'Voice', hint: 'TTS engine & voice' },
  { label: 'Video', hint: 'Mode, avatar & output' },
  { label: 'Review', hint: 'Confirm & submit' },
];

const defaultConfig: TranslationConfig = {
  target: 'hi',
  stt: 'sarvam',
  mt: 'sarvam',
  tts: 'elevenlabs',
  voice: '',
  cloneVoice: true,
  lipSync: false,
  videoMode: 'dubbed',
};

interface VideoTranslationWizardProps {
  avatars: Avatar[];
  voices: Voice[];
  voicesLoading: boolean;
  onJobStarted: (jobId: string) => void;
  /** Pre-selects a source video (e.g. from the Dashboard's "Translate" action or the Videos page) and jumps straight past the template gate into the wizard. */
  initialUpload?: PreselectedUpload;
}

export const VideoTranslationWizard: React.FC<VideoTranslationWizardProps> = ({
  avatars,
  voices,
  voicesLoading,
  onJobStarted,
  initialUpload,
}) => {
  const navigate = useNavigate();
  // Always start at the template gate, even with a video already picked (from the
  // Dashboard or Videos page) — the preselected video is retained and carried
  // through into whichever path (template or scratch) the user chooses there.
  const [phase, setPhase] = useState<'gate' | 'wizard' | 'done'>('gate');
  const [step, setStep] = useState(0);

  const [config, setConfig] = useState<TranslationConfig>(defaultConfig);
  const [appliedTemplate, setAppliedTemplate] = useState<TranslationTemplate | null>(null);
  const [savedTemplates, setSavedTemplates] = useState<TranslationTemplate[]>([]);
  const [hydrated, setHydrated] = useState(false);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preselectedUpload, setPreselectedUpload] = useState<PreselectedUpload | null>(() => initialUpload || null);
  const [sourceMode, setSourceMode] = useState<'file' | 'url' | 'audio'>('file');
  const [videoUrl, setVideoUrl] = useState('');
  const [fetchingUrl, setFetchingUrl] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [fetchingAudioUrl, setFetchingAudioUrl] = useState(false);
  const [isAudioOnlySource, setIsAudioOnlySource] = useState(false);
  const [title, setTitle] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState('auto');

  const [narrationMode, setNarrationMode] = useState<NarrationMode>('formal');
  const [requireTranslationReview, setRequireTranslationReview] = useState(false);
  const [ttsSpeed, setTtsSpeed] = useState(1.0);

  const [outputFormat, setOutputFormat] = useState<'mp4' | 'avi' | 'mov' | 'webm'>('mp4');
  const [videoQuality, setVideoQuality] = useState<'low' | 'medium' | 'high' | 'original'>('medium');
  const [lipsyncModel, setLipsyncModel] = useState<'wav2lip' | 'video-retalking'>('video-retalking');
  const [enhanceAudio, setEnhanceAudio] = useState(false);
  const [preserveEmotion, setPreserveEmotion] = useState(true);
  const [customPrompt, setCustomPrompt] = useState('');
  const [avatarId, setAvatarId] = useState<string | undefined>(undefined);
  const [refPoseFile, setRefPoseFile] = useState<File | null>(null);

  const [templateName, setTemplateName] = useState('');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [refPoseUploadProgress, setRefPoseUploadProgress] = useState<number | null>(null);

  useEffect(() => {
    setSavedTemplates(loadSavedTemplates());
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!initialUpload) return;
    setSelectedFile(null);
    setPreselectedUpload(initialUpload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUpload]);

  const activeVoices = useMemo(() => {
    const ready = voices.filter((v) => v.isActive && v.status === 'ready');
    return ready.filter((v) => v.provider === config.tts);
  }, [voices, config.tts]);

  const setConfigValue = <K extends keyof TranslationConfig>(key: K, value: TranslationConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  // An audio-URL-sourced job has no video frames, so Dubbed/Lipsynced — which need
  // the original frames — aren't reachable. Avatar mode is fully synthesized from
  // the audio, so it's the only mode that works here.
  useEffect(() => {
    if (isAudioOnlySource && config.videoMode !== 'avatar') {
      setConfigValue('videoMode', 'avatar');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAudioOnlySource]);

  const canContinue = useMemo(() => {
    switch (step) {
      case 0:
        return !!(selectedFile || preselectedUpload);
      case 1:
        return !!config.stt;
      case 2:
        return !!config.mt;
      case 3:
        return !!config.tts && !!config.voice;
      case 4:
        return config.videoMode !== 'avatar' || !!avatarId;
      case 5:
        return !!config.voice && !!(selectedFile || preselectedUpload);
      default:
        return false;
    }
  }, [step, selectedFile, preselectedUpload, config.stt, config.mt, config.tts, config.voice, config.videoMode, avatarId]);

  const maxReached = appliedTemplate ? STEP_LABELS.length - 1 : step;

  const uploadMutation = useMutation({
    mutationFn: (file: File) =>
      file.type.startsWith('audio/')
        ? videoApi.uploadAudioFile(file, (pct) => setUploadProgress(pct))
        : videoApi.uploadVideo(file, (pct) => setUploadProgress(pct)),
    onError: (error: any) => {
      setUploadProgress(null);
      toast.error(error.response?.data?.message || 'Failed to upload file');
    },
  });

  const refPoseMutation = useMutation({
    mutationFn: (file: File) => videoApi.uploadRefPoseVideo(file, (pct) => setRefPoseUploadProgress(pct)),
    onError: (error: any) => {
      setRefPoseUploadProgress(null);
      toast.error(error.response?.data?.message || 'Failed to upload reference pose video');
    },
  });

  const startTranslationMutation = useMutation({
    mutationFn: (params: Parameters<typeof videoApi.startTranslation>[0]) => videoApi.startTranslation(params),
    onSuccess: (data) => {
      toast.success('Video translation started successfully!');
      onJobStarted(data.jobId);
      setPhase('done');
      resetAll();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to start video translation');
    },
  });

  const isSubmitting = uploadMutation.isLoading || refPoseMutation.isLoading || startTranslationMutation.isLoading;

  const resetAudioUrlState = () => {
    setAudioUrl('');
    setFetchingAudioUrl(false);
    setIsAudioOnlySource(false);
  };

  const handleUseTemplate = (template: TranslationTemplate) => {
    setConfig(template.config);
    setAppliedTemplate(template);
    setPhase('wizard');
    setStep(0);
    setSelectedFile(null);
    // Carry a video picked before the gate (Dashboard/Videos page) through into
    // the wizard rather than discarding it — only a raw File selection can't
    // survive this, since it never leaves the browser as a fileId.
    setPreselectedUpload(initialUpload || null);
    setSourceMode('file');
    setVideoUrl('');
    resetAudioUrlState();
  };

  const resetWizardFields = () => {
    setConfig(defaultConfig);
    setAppliedTemplate(null);
    setStep(0);
    setSelectedFile(null);
    setPreselectedUpload(initialUpload || null);
    setSourceMode('file');
    setVideoUrl('');
    setFetchingUrl(false);
    resetAudioUrlState();
    setTitle('');
    setSourceLanguage('auto');
    setTtsSpeed(1.0);
    setNarrationMode('formal');
    setRequireTranslationReview(false);
    setOutputFormat('mp4');
    setVideoQuality('medium');
    setLipsyncModel('video-retalking');
    setEnhanceAudio(false);
    setPreserveEmotion(true);
    setCustomPrompt('');
    setAvatarId(undefined);
    setRefPoseFile(null);
    setTemplateName('');
  };

  const handleStartFromScratch = () => {
    resetWizardFields();
    setPhase('wizard');
  };

  const handleCancelWizard = () => {
    resetWizardFields();
    // Return to wherever this page was navigated from (Dashboard's quick-start,
    // the Videos page's "Translate" action, etc.) rather than staying here.
    navigate(-1);
  };

  const resetAll = () => {
    setSelectedFile(null);
    setPreselectedUpload(null);
    setSourceMode('file');
    setVideoUrl('');
    setFetchingUrl(false);
    resetAudioUrlState();
    setTitle('');
    setSourceLanguage('auto');
    setUploadProgress(null);
    setRefPoseUploadProgress(null);
  };

  const handleSaveTemplate = () => {
    const name = templateName.trim();
    if (!name) return;
    const newTemplate: TranslationTemplate = {
      id: `tpl-${Date.now()}`,
      name,
      description: 'Saved from a previous translation.',
      config,
    };
    const next = [newTemplate, ...savedTemplates.filter((t) => t.name !== name)];
    setSavedTemplates(next);
    persistSavedTemplates(next);
    toast.success(`Template “${name}” saved`);
  };

  const handleRemoveTemplate = (id: string) => {
    const next = savedTemplates.filter((t) => t.id !== id);
    setSavedTemplates(next);
    persistSavedTemplates(next);
  };

  const handleSubmit = async () => {
    let fileId: string | undefined;
    let refPoseFileId: string | undefined;

    try {
      if (selectedFile) {
        setUploadProgress(0);
        const uploadResult = await uploadMutation.mutateAsync(selectedFile);
        fileId = uploadResult.fileId;
      } else if (preselectedUpload) {
        fileId = preselectedUpload.fileId;
      }

      if (refPoseFile && config.videoMode === 'avatar') {
        setRefPoseUploadProgress(0);
        const refResult = await refPoseMutation.mutateAsync(refPoseFile);
        refPoseFileId = refResult.refPoseFileId;
      }

      await startTranslationMutation.mutateAsync({
        ...(fileId ? { fileId } : {}),
        title,
        targetLanguage: config.target,
        voiceId: config.voice,
        sourceLanguage: sourceLanguage !== 'auto' ? sourceLanguage : undefined,
        sttProvider: config.stt,
        translationProvider: config.mt,
        ...(config.mt === 'sarvam' ? { narrationMode } : {}),
        ttsProvider: config.tts,
        ttsSpeed,
        outputFormat,
        videoQuality,
        lipsyncModel,
        enhanceAudio,
        preserveEmotion,
        customPrompt,
        videoMode: config.videoMode,
        avatarId: config.videoMode === 'avatar' ? avatarId : undefined,
        refPoseFileId,
        requireTranslationReview,
      });
    } catch {
      setUploadProgress(null);
      setRefPoseUploadProgress(null);
    }
  };

  const handleFetchVideoUrl = async () => {
    const url = videoUrl.trim();
    if (!url) {
      toast.error('Please enter a video URL');
      return;
    }
    setFetchingUrl(true);
    try {
      const res = await videoApi.uploadVideoFromUrl(url);
      setPreselectedUpload({ fileId: res.fileId, name: res.originalName, size: res.size });
      setSelectedFile(null);
      setIsAudioOnlySource(false);
      setVideoUrl('');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch video from that URL');
    } finally {
      setFetchingUrl(false);
    }
  };

  const handleFetchAudioUrl = async () => {
    const url = audioUrl.trim();
    if (!url) {
      toast.error('Please enter an audio URL');
      return;
    }
    setFetchingAudioUrl(true);
    try {
      const res = await videoApi.uploadAudioFromUrl(url);
      setPreselectedUpload({ fileId: res.fileId, name: res.originalName, size: res.size });
      setSelectedFile(null);
      setIsAudioOnlySource(true);
      setAudioUrl('');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch audio from that URL');
    } finally {
      setFetchingAudioUrl(false);
    }
  };

  const changedFromTemplate = useMemo(
    () => (appliedTemplate ? diffConfig(config, appliedTemplate.config) : []),
    [config, appliedTemplate]
  );

  const renderGate = () => (
    <TemplateGate
      builtIn={builtInTemplates}
      saved={savedTemplates}
      onUse={handleUseTemplate}
      onRemove={handleRemoveTemplate}
      onScratch={handleStartFromScratch}
      onCancel={handleCancelWizard}
      preselectedVideoName={initialUpload?.name}
    />
  );

  const renderDone = () => (
    <div className="py-16 text-center animate-fade-in">
      <CheckCircleIcon className="mx-auto h-14 w-14 text-emerald-500" />
      <h2 className="mt-5 text-2xl font-semibold text-gray-900 dark:text-white">Job queued</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
        Your video is being transcribed, translated and re-voiced into {getLanguageName(config.target)}.
      </p>
      <button
        type="button"
        onClick={handleStartFromScratch}
        className="mt-6 inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
      >
        Start another translation
      </button>
    </div>
  );

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return renderSourceStep();
      case 1:
        return renderSttStep();
      case 2:
        return renderTranslationStep();
      case 3:
        return renderVoiceStep();
      case 4:
        return renderVideoStep();
      case 5:
        return renderReviewStep();
      default:
        return null;
    }
  };

  const renderSourceStep = () => {
    const hasSource = !!(selectedFile || preselectedUpload);

    return (
    <div className="space-y-4">
      <StepHead
        title="Add your source"
        sub="Upload a video or audio file, or paste a direct link to one. Clean, single-speaker audio gives the best dub."
      />

      {!hasSource && (
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => setSourceMode('file')}
            className={cn(
              'flex items-center justify-center gap-1.5 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-all',
              sourceMode === 'file'
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/20 dark:text-indigo-300'
                : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500'
            )}
          >
            <CloudArrowUpIcon className="h-4 w-4" /> Upload file
          </button>
          <button
            type="button"
            onClick={() => setSourceMode('url')}
            className={cn(
              'flex items-center justify-center gap-1.5 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-all',
              sourceMode === 'url'
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/20 dark:text-indigo-300'
                : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500'
            )}
          >
            <LinkIcon className="h-4 w-4" /> Video URL
          </button>
          <button
            type="button"
            onClick={() => setSourceMode('audio')}
            className={cn(
              'flex items-center justify-center gap-1.5 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-all',
              sourceMode === 'audio'
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/20 dark:text-indigo-300'
                : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500'
            )}
          >
            <SpeakerWaveIcon className="h-4 w-4" /> Audio URL
          </button>
        </div>
      )}

      {(hasSource || sourceMode === 'file') && (
        <VideoUploadZone
          acceptAudio
          onFileSelect={(file) => {
            setSelectedFile(file);
            if (file) {
              setPreselectedUpload(null);
              setIsAudioOnlySource(file.type.startsWith('audio/'));
            } else {
              setIsAudioOnlySource(false);
            }
          }}
          selectedFile={selectedFile}
          isUploading={isSubmitting}
          uploadProgress={uploadProgress ?? undefined}
          preselectedUpload={preselectedUpload}
          onClearPreselectedUpload={() => {
            setPreselectedUpload(null);
            setIsAudioOnlySource(false);
          }}
        />
      )}

      {!hasSource && sourceMode === 'url' && (
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Video URL</label>
          <div className="flex gap-2">
            <input
              type="url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleFetchVideoUrl();
                }
              }}
              placeholder="https://example.com/video.mp4"
              disabled={fetchingUrl}
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleFetchVideoUrl}
              disabled={fetchingUrl || !videoUrl.trim()}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {fetchingUrl ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" /> Fetching…
                </>
              ) : (
                'Fetch Video'
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Paste a direct link to a video file (e.g. ends in .mp4, .mov, .webm). YouTube and other streaming or social platforms aren&apos;t supported.
          </p>
        </div>
      )}

      {!hasSource && sourceMode === 'audio' && (
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Audio URL</label>
          <div className="flex gap-2">
            <input
              type="url"
              value={audioUrl}
              onChange={(e) => setAudioUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleFetchAudioUrl();
                }
              }}
              placeholder="https://example.com/audio.mp3"
              disabled={fetchingAudioUrl}
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleFetchAudioUrl}
              disabled={fetchingAudioUrl || !audioUrl.trim()}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {fetchingAudioUrl ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" /> Fetching…
                </>
              ) : (
                'Fetch Audio'
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Paste a direct link to an audio file (e.g. ends in .mp3, .wav, .m4a). This always
            produces an Avatar-mode translation — Dubbed and Lipsynced aren&apos;t available for
            audio sources, since there&apos;s no source video to dub or lip-sync onto.
          </p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Source language</label>
          <select
            value={sourceLanguage}
            onChange={(e) => setSourceLanguage(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            <option value="auto">Auto-detect</option>
            {targetLanguages.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Target language *</label>
          <select
            value={config.target}
            onChange={(e) => setConfigValue('target', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            {targetLanguages.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Title <span className="text-gray-400">(optional)</span>
          </label>
          <LocalizedTextInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Product Demo - Hindi"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            maxLength={200}
          />
        </div>
      </div>

      <HelpNote>Background music is fine — we separate the stems and keep the original score underneath the new voice track.</HelpNote>
    </div>
    );
  };

  const renderSttStep = () => (
    <div className="space-y-5">
      <StepHead
        title="Choose a speech-to-text engine"
        sub="This produces the transcript everything else is built on — accuracy here matters most."
      />
      <EnginePicker engines={sttEngines} value={config.stt} onChange={(id) => setConfigValue('stt', id as SttProvider)} />
      <HelpNote>
        Noisy or accented audio? AI4Bharat IndicConformer recovers more Indic words. For fast cloud processing of mixed content, Sarvam is the best default.
      </HelpNote>
    </div>
  );

  const renderTranslationStep = () => (
    <div className="space-y-5">
      <StepHead title="Choose a translation engine" sub={`Translating your transcript into ${getLanguageName(config.target)}.`} />
      <EnginePicker engines={mtEngines} value={config.mt} onChange={(id) => setConfigValue('mt', id as MtProvider)} />

      {config.mt === 'sarvam' && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Narration mode</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {NARRATION_MODES.map((m) => {
              const active = narrationMode === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setNarrationMode(m.value)}
                  className={cn(
                    'flex flex-col items-start gap-0.5 rounded-lg border-2 px-3 py-2.5 text-left text-xs font-medium transition-all',
                    active
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/20 dark:text-indigo-300'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500'
                  )}
                >
                  <span className="flex items-center gap-1.5 font-semibold">
                    {m.label}
                    {active && <CheckIcon className="h-3.5 w-3.5" />}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400 font-normal">{m.description}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Translated text verification</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setRequireTranslationReview(false)}
            className={cn(
              'flex flex-col items-start gap-0.5 rounded-lg border-2 px-3 py-2.5 text-left text-xs font-medium transition-all',
              !requireTranslationReview
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/20 dark:text-indigo-300'
                : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500'
            )}
          >
            <span className="flex items-center gap-1.5 font-semibold">
              Run all steps automatically
              {!requireTranslationReview && <CheckIcon className="h-3.5 w-3.5" />}
            </span>
            <span className="text-gray-500 dark:text-gray-400 font-normal">Skip verification — speech, translation, voice and video generation run back-to-back.</span>
          </button>
          <button
            type="button"
            onClick={() => setRequireTranslationReview(true)}
            className={cn(
              'flex flex-col items-start gap-0.5 rounded-lg border-2 px-3 py-2.5 text-left text-xs font-medium transition-all',
              requireTranslationReview
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/20 dark:text-indigo-300'
                : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500'
            )}
          >
            <span className="flex items-center gap-1.5 font-semibold">
              Let me verify the translation
              {requireTranslationReview && <CheckIcon className="h-3.5 w-3.5" />}
            </span>
            <span className="text-gray-500 dark:text-gray-400 font-normal">Pause after translation so you can review or edit the text before voice generation continues.</span>
          </button>
        </div>
      </div>

      <HelpNote>
        Marketing or brand-heavy scripts do best with a context-aware engine. For legal or technical content, DeepL with a glossary locks exact terminology.
      </HelpNote>
    </div>
  );

  const renderVoiceStep = () => (
    <div className="space-y-6">
      <StepHead title="Choose a voice engine" sub="The final dub. Match the energy of your original speaker." />
      <EnginePicker engines={ttsEngines} value={config.tts} onChange={(id) => setConfigValue('tts', id as TtsProvider)} />

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Voice *</label>
        <select
          value={config.voice}
          onChange={(e) => setConfigValue('voice', e.target.value)}
          disabled={voicesLoading}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          <option value="" disabled>
            {voicesLoading ? 'Loading voices...' : 'Select voice'}
          </option>
          {activeVoices.map((voice) => (
            <option key={voice._id} value={voice._id}>
              {voice.name} {voice.gender ? `(${voice.gender})` : ''} {voice.language ? `- ${voice.language}` : ''}
            </option>
          ))}
        </select>
        {activeVoices.length === 0 && !voicesLoading && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            No {engineName(config.tts, 'tts')} voices found. Please add a voice first.
          </p>
        )}
      </div>

      {config.tts !== 'ai4bharat' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Speech speed</label>
            <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">{ttsSpeed.toFixed(1)}x</span>
          </div>
          <input
            type="range"
            min="0.5"
            max="2.0"
            step="0.1"
            value={ttsSpeed}
            onChange={(e) => setTtsSpeed(parseFloat(e.target.value))}
            className="w-full h-2 accent-indigo-600 dark:accent-indigo-400 cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>Slow (0.5x)</span>
            <span>Normal (1.0x)</span>
            <span>Fast (2.0x)</span>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Voice options</h3>
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={enhanceAudio}
              onChange={(e) => setEnhanceAudio(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Enhance audio quality</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={preserveEmotion}
              onChange={(e) => setPreserveEmotion(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Preserve emotional tone</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.cloneVoice}
              onChange={(e) => setConfigValue('cloneVoice', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Clone the original voice</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderVideoStep = () => (
    <div className="space-y-6">
      <StepHead title="Choose a video generation mode" sub="Decide how the final video should look, then set output options." />

      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Video generation mode</h3>
        {isAudioOnlySource && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Only Avatar mode is available for audio sources — Dubbed and Lipsynced need the
            original video frames, which an audio-only source doesn&apos;t have.
          </p>
        )}
        <div className="flex gap-3 overflow-x-auto pb-1">
          {(isAudioOnlySource ? VIDEO_MODES.filter((m) => m.mode === 'avatar') : VIDEO_MODES).map(({ mode, label, description, icon }) => {
            const active = config.videoMode === mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => setConfigValue('videoMode', mode)}
                className={cn(
                  'relative flex min-w-[220px] flex-1 shrink-0 items-start gap-3 rounded-lg border-2 p-3 text-left transition-all',
                  active
                    ? 'border-indigo-600 bg-indigo-50 ring-1 ring-indigo-500 dark:border-indigo-500 dark:bg-indigo-900/20'
                    : 'border-gray-200 hover:border-indigo-300 dark:border-gray-600 dark:hover:border-indigo-500'
                )}
              >
                <div className={cn('shrink-0', active ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500')}>
                  {icon}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{label}</p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 leading-snug">{description}</p>
                </div>
                {active && <CheckCircleIcon className="absolute top-2 right-2 h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400" />}
              </button>
            );
          })}
        </div>
      </div>

      {config.videoMode === 'avatar' && (
        <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 dark:border-purple-800 dark:bg-purple-900/10">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-purple-700 dark:text-purple-300 flex items-center gap-1.5">
              <FaceSmileIcon className="h-4 w-4" /> Select Avatar <span className="text-red-500">*</span>
            </p>
            <a
              href="/app/audio/avatars"
              className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 dark:text-purple-400"
            >
              Manage Avatars <ArrowTopRightOnSquareIcon className="h-3 w-3" />
            </a>
          </div>
          {avatars.length === 0 ? (
            <div className="text-center py-4 text-sm text-gray-400">
              No avatars found. <a href="/app/audio/avatars" className="text-purple-600 hover:text-purple-700 underline">Upload one</a>
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 max-h-40 overflow-y-auto pr-1 mt-3">
              {avatars.map((avatar) => {
                const isSelected = avatarId === avatar._id;
                const imgSrc = avatarApi.getImageSrc(avatar.imageUrl);
                return (
                  <button
                    key={avatar._id}
                    type="button"
                    onClick={() => setAvatarId(avatar._id)}
                    className={cn(
                      'relative aspect-square overflow-hidden rounded-lg border-2 transition-all',
                      isSelected ? 'border-purple-500 ring-2 ring-purple-300 dark:ring-purple-700' : 'border-gray-200 dark:border-gray-600 hover:border-purple-300'
                    )}
                    title={avatar.name}
                  >
                    {imgSrc ? (
                      <img src={imgSrc} alt={avatar.name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gray-100 dark:bg-gray-700">
                        <UserCircleIcon className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute inset-0 flex items-center justify-center bg-purple-500/20">
                        <CheckCircleIcon className="h-5 w-5 text-purple-600" />
                      </div>
                    )}
                    {avatar.isLibrary && (
                      <span className="absolute bottom-0 left-0 right-0 bg-purple-600 text-center text-[9px] leading-tight text-white py-0.5">
                        Library
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-4 border-t border-purple-200 pt-3 dark:border-purple-700">
            <p className="text-xs font-medium text-purple-700 dark:text-purple-300 flex items-center gap-1.5 mb-2">
              <VideoCameraIcon className="h-4 w-4" /> Reference pose video <span className="font-normal text-gray-500">(optional)</span>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Upload a video to copy its head movement &amp; expressions onto your avatar.
            </p>
            {refPoseFile ? (
              <div className="flex items-center justify-between rounded-lg bg-purple-100 px-3 py-2 dark:bg-purple-900/30">
                <div className="flex items-center gap-2 min-w-0">
                  <VideoCameraIcon className="h-4 w-4 flex-shrink-0 text-purple-500" />
                  <span className="truncate text-xs text-purple-700 dark:text-purple-300">{refPoseFile.name}</span>
                  <span className="flex-shrink-0 text-xs text-gray-400">({(refPoseFile.size / 1024 / 1024).toFixed(1)} MB)</span>
                </div>
                <button
                  type="button"
                  onClick={() => setRefPoseFile(null)}
                  className="ml-2 flex-shrink-0 text-gray-400 hover:text-red-500"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-purple-300 px-3 py-2 hover:border-purple-400 dark:border-purple-700 dark:hover:border-purple-500">
                <VideoCameraIcon className="h-4 w-4 text-purple-400" />
                <span className="text-xs text-purple-600 dark:text-purple-400">Click to select a reference video</span>
                <input
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setRefPoseFile(f);
                    e.target.value = '';
                  }}
                />
              </label>
            )}
            {refPoseUploadProgress !== null && (
              <div className="mt-1.5">
                <div className="h-1 w-full rounded-full bg-purple-200 dark:bg-purple-800">
                  <div className="h-1 rounded-full bg-purple-500 transition-all" style={{ width: `${refPoseUploadProgress}%` }} />
                </div>
                <p className="mt-0.5 text-xs text-purple-500">Uploading reference... {refPoseUploadProgress}%</p>
              </div>
            )}
          </div>
        </div>
      )}

      {config.videoMode !== 'dubbed' && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Lip-sync model</label>
          <select
            value={lipsyncModel}
            onChange={(e) => setLipsyncModel(e.target.value as typeof lipsyncModel)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            <option value="video-retalking">Video-ReTalking (recommended)</option>
            <option value="wav2lip">Wav2Lip (faster)</option>
          </select>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Output format</label>
          <select
            value={outputFormat}
            onChange={(e) => setOutputFormat(e.target.value as typeof outputFormat)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            <option value="mp4">MP4</option>
            <option value="avi">AVI</option>
            <option value="mov">MOV</option>
            <option value="webm">WebM</option>
          </select>
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Video quality</label>
          <select
            value={videoQuality}
            onChange={(e) => setVideoQuality(e.target.value as typeof videoQuality)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            <option value="low">Low (fast)</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="original">Original (slow)</option>
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Custom translation prompt (optional)</label>
        <LocalizedTextArea
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          placeholder="Add specific instructions for translation..."
          rows={3}
          maxLength={1000}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        />
        <p className="text-xs text-gray-500">{customPrompt.length}/1000 characters</p>
      </div>
    </div>
  );

  const renderReviewStep = () => {
    const summary = [
      { icon: FilmIcon, label: 'Video', value: selectedFile?.name || preselectedUpload?.name || '—' },
      { icon: ArrowPathIcon, label: 'Source language', value: sourceLanguage === 'auto' ? 'Auto-detect' : getLanguageName(sourceLanguage) },
      { icon: ArrowPathIcon, label: 'Target language', value: getLanguageName(config.target) },
      { icon: ArrowPathIcon, label: 'Transcription', value: engineName(config.stt, 'stt') },
      { icon: ArrowPathIcon, label: 'Translation', value: engineName(config.mt, 'mt') },
      { icon: ArrowPathIcon, label: 'Voice', value: voices.find((v) => v._id === config.voice)?.name || '—' },
      { icon: ArrowPathIcon, label: 'Mode', value: VIDEO_MODES.find((m) => m.mode === config.videoMode)?.label || config.videoMode },
      { icon: ArrowPathIcon, label: 'Options', value: [config.cloneVoice && 'cloned voice', enhanceAudio && 'enhanced audio', preserveEmotion && 'preserve emotion'].filter(Boolean).join(' · ') || '—' },
    ];

    return (
      <div className="space-y-5">
        <StepHead title="Review & submit" sub="Everything below is editable — click any step above to change it." />

        {!canContinue && step === 4 && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200">
            Add a source video and choose a voice before submitting.
          </div>
        )}

        <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
          <div className="grid gap-3 sm:grid-cols-2">
            {summary.map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-center gap-3">
                <Icon className="h-4 w-4 shrink-0 text-indigo-500" />
                <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
                <span className="ml-auto truncate text-sm font-medium text-gray-900 dark:text-white">{value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
            <BookmarkIcon className="h-4 w-4 text-indigo-600" /> Save these settings as a template
          </label>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g., Spanish product demos"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Optional. Saved templates appear on the start screen.
            </p>
            <button
              type="button"
              disabled={!templateName.trim()}
              onClick={handleSaveTemplate}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              Save
            </button>
          </div>
        </div>

        <HelpNote>Templates never include the video itself — only engines, language and voice options.</HelpNote>
      </div>
    );
  };

  const renderWizard = () => (
    <div className="space-y-6">
      {appliedTemplate && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 dark:border-indigo-900/40 dark:bg-indigo-900/20">
          <BookmarkIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          <p className="text-sm text-gray-900 dark:text-white">
            Using template <span className="font-medium">{appliedTemplate.name}</span>
          </p>
          {changedFromTemplate.length > 0 && (
            <span className="rounded-md bg-amber-100 px-2 py-0.5 text-[11px] text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              {changedFromTemplate.length} setting{changedFromTemplate.length > 1 ? 's' : ''} modified
            </span>
          )}
          <div className="ml-auto flex gap-2">
            {changedFromTemplate.length > 0 && (
              <button
                type="button"
                onClick={() => setConfig(appliedTemplate.config)}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100 dark:text-indigo-300 dark:hover:bg-indigo-900/40"
              >
                <ArrowPathIcon className="h-3.5 w-3.5" /> Reset
              </button>
            )}
            <button
              type="button"
              onClick={() => setAppliedTemplate(null)}
              className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Detach
            </button>
          </div>
        </div>
      )}

      <WizardStepper steps={STEP_LABELS} current={step} maxReached={maxReached} onJump={setStep} />

      <div className="mt-6 h-1 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          className="h-full rounded-full bg-indigo-600 transition-all duration-500 dark:bg-indigo-500"
          style={{ width: `${((step + (canContinue ? 1 : 0.35)) / STEP_LABELS.length) * 100}%` }}
        />
      </div>

      <div className="animate-fade-in h-[480px] overflow-y-auto pr-1">{renderStepContent()}</div>

      <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-6 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleCancelWizard}
            disabled={isSubmitting}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-400 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => (step === 0 ? setPhase('gate') : setStep((s) => s - 1))}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4" /> {step === 0 ? 'Templates' : 'Back'}
          </button>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs text-gray-500 dark:text-gray-400 sm:block">
            Step {step + 1} of {STEP_LABELS.length}
          </span>
          {step < STEP_LABELS.length - 1 ? (
            <>
              {appliedTemplate && (
                <button
                  type="button"
                  disabled={!canContinue}
                  onClick={() => setStep(STEP_LABELS.length - 1)}
                  className="inline-flex items-center gap-1 rounded-lg border border-indigo-600 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-400 dark:text-indigo-400 dark:hover:bg-indigo-900/20"
                >
                  Skip to Review <ArrowRightIcon className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                disabled={!canContinue}
                onClick={() => setStep((s) => Math.min(STEP_LABELS.length - 1, s + 1))}
                className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                Continue <ArrowRightIcon className="h-4 w-4" />
              </button>
            </>
          ) : (
            <button
              type="button"
              disabled={!canContinue || isSubmitting}
              onClick={handleSubmit}
              className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              {isSubmitting ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" /> Submitting…
                </>
              ) : (
                <>
                  <RocketLaunchIcon className="h-4 w-4" /> Start translation
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (!hydrated) return null;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {phase === 'gate' && renderGate()}
        {phase === 'wizard' && renderWizard()}
        {phase === 'done' && renderDone()}
      </div>
    </div>
  );
};

function StepHead({ title, sub }: { title: string; sub: string }) {
  return (
    <div>
      <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">{title}</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{sub}</p>
    </div>
  );
}

export default VideoTranslationWizard;
